import { useEffect, useMemo } from 'react'
import { formatDateToPtBr, initialQueryState, useDebounce } from '_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  DateInput,
  Input,
  TableFilter,
  TableFilters,
} from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'

export const LogsConsultaListSearchComponent = () => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(() => {
    const { paciente, protocolo, responsavel, solicitacao } = debouncedFilters
    return [
      protocolo.trim() ? `protocolo~*${protocolo.trim()}*` : '',
      paciente.trim() ? `paciente.pessoa.nome~*${paciente.trim()}*` : '',
      solicitacao ? `solicitacao>${solicitacao}T00:00:00*` : '',
      responsavel.trim() ? `responsavel.pessoa.nome~*${responsavel.trim()}*` : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo. Data pura via split
  // (formatDateToPtBr), sem `new Date`, para não voltar um dia no fuso do Brasil.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.responsavel.trim() && {
      key: 'responsavel',
      label: t('cols.responsavel'),
      value: `“${filters.responsavel.trim()}”`,
      onRemove: () => setFilter('responsavel', ''),
    },
    filters.solicitacao && {
      key: 'solicitacao',
      label: 'Solicitação a partir de',
      value: formatDateToPtBr(filters.solicitacao),
      onRemove: () => setFilter('solicitacao', ''),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      moreFiltersActiveCount={filters.solicitacao ? 1 : 0}
      moreFilters={
        <TableFilter label='Solicitação a partir de'>
          <DateInput
            value={filters.solicitacao}
            onChange={(e) => setFilter('solicitacao', e.target.value)}
          />
        </TableFilter>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.responsavel')}>
        <Input
          placeholder='Digite o responsável'
          value={filters.responsavel}
          onChange={(e) => setFilter('responsavel', e.target.value)}
        />
      </TableFilter>
    </TableFilters>
  )
}
